import TriangleIcon from "@mui/icons-material/ChangeHistory";
import CircleIcon from "@mui/icons-material/FiberManualRecord";
import { styled } from "@mui/material";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef } from "react";

import { CssGrid, GapSize, Orientation, Position } from "./cssGrid";

import { pressKey, releaseKey } from "../core/retroboyCore";
import { useIsTablet } from "../hooks/useResponsiveBreakpoint";
import { gameControls } from "../modals/controlsModal";

const GamePadWrapperGrid = styled(CssGrid)`
    background: transparent;
    padding: 4px 8px;
    width: 100%;
`;

const CircularButtonGrid = styled(CssGrid)`
    margin-top: 32px;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    background: rgba(255, 255, 255, 0.88);
    color: #888;
    font-size: 28px;
    font-weight: bold;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
    transition: all 0.1s ease-in-out;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
    touch-action: none;

    &:active {
        transform: scale(0.92);
        background: rgba(240, 240, 240, 0.95);
        box-shadow:
            inset 0 2px 4px rgba(0, 0, 0, 0.2),
            0 1px 2px rgba(0, 0, 0, 0.2);
    }
`;

const BGrid = styled(CircularButtonGrid)`
    margin-top: 48px;
`;

const AGrid = styled(CircularButtonGrid)`
    margin-top: 16px;
`;

const CylindricalButtonGrid = styled(CssGrid)`
    width: 72px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 11px;
    letter-spacing: 1px;
`;

const CylindricalButtonGridArea = styled("div")`
    height: 28px;
    width: 64px;
    border-radius: 40%;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
    transition: all 0.1s ease-in-out;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
    touch-action: none;

    &:active {
        transform: scale(0.92);
        background: rgba(240, 240, 240, 0.95);
        box-shadow:
            inset 0 2px 4px rgba(0, 0, 0, 0.2),
            0 1px 2px rgba(0, 0, 0, 0.2);
    }
`;

const DirectionalPadGrid = styled(CssGrid)`
    margin-top: 16px;
    line-height: 0;
`;

const DirectionalPadArea = styled("div")`
    background: rgba(255, 255, 255, 0.88);
    transition: all 0.1s ease-in-out;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
    touch-action: none;
    display: flex;
    align-items: center;
    justify-content: center;

    &:active {
        transform: scale(0.95);
        background: rgba(240, 240, 240, 0.95);
    }
`;

const DirectionalIcon = styled(TriangleIcon, {
    shouldForwardProp: prop => prop !== "rotation",
})<{ rotation: number }>(({ rotation }) => ({
    transform: `rotate(${rotation}deg)`,
    color: "#999",
}));

const DirectionalPadCenterArea = styled("div")`
    background: rgba(255, 255, 255, 0.88);
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DirectionalPadCenterIcon = styled(CircleIcon)`
    color: #999;
`;

const gameControlClass = "game-control";

const DirectionalPad = ({
    onPointerStart,
    onPointerEnd,
}: DirectionalPadProps): JSX.Element => {
    return (
        <DirectionalPadGrid
            orientation={Orientation.horizontal}
            template="1fr 1fr 1fr"
        >
            <div />
            <DirectionalPadArea
                aria-label={gameControls.up}
                onPointerDown={event => onPointerStart(gameControls.up, event)}
                onPointerUp={event => onPointerEnd(gameControls.up, event)}
                onPointerCancel={event => onPointerEnd(gameControls.up, event)}
                className={gameControlClass}
            >
                <DirectionalIcon rotation={0} />
            </DirectionalPadArea>
            <div />
            <DirectionalPadArea
                aria-label={gameControls.left}
                onPointerDown={event =>
                    onPointerStart(gameControls.left, event)
                }
                onPointerUp={event => onPointerEnd(gameControls.left, event)}
                onPointerCancel={event =>
                    onPointerEnd(gameControls.left, event)
                }
                className={gameControlClass}
            >
                <DirectionalIcon rotation={270} />
            </DirectionalPadArea>
            <DirectionalPadCenterArea>
                <DirectionalPadCenterIcon />
            </DirectionalPadCenterArea>
            <DirectionalPadArea
                aria-label={gameControls.right}
                onPointerDown={event =>
                    onPointerStart(gameControls.right, event)
                }
                onPointerUp={event => onPointerEnd(gameControls.right, event)}
                onPointerCancel={event =>
                    onPointerEnd(gameControls.right, event)
                }
                className={gameControlClass}
            >
                <DirectionalIcon rotation={90} />
            </DirectionalPadArea>
            <div />
            <DirectionalPadArea
                aria-label={gameControls.down}
                onPointerDown={event =>
                    onPointerStart(gameControls.down, event)
                }
                onPointerUp={event => onPointerEnd(gameControls.down, event)}
                onPointerCancel={event =>
                    onPointerEnd(gameControls.down, event)
                }
                className={gameControlClass}
            >
                <DirectionalIcon rotation={180} />
            </DirectionalPadArea>
            <div />
        </DirectionalPadGrid>
    );
};

const GamePad = ({ playing }: GamePadProps): JSX.Element => {
    const isTablet = useIsTablet();

    const activeControlsRef = useRef(new Set<string>());

    const releaseActiveControls = (): void => {
        activeControlsRef.current.forEach(gameControl =>
            releaseKey(gameControl),
        );
        activeControlsRef.current.clear();
    };

    const handlePointerStart = (
        gameControl: string,
        event: ReactPointerEvent<HTMLDivElement>,
    ): void => {
        if (event.button !== 0) {
            return;
        }

        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);

        if (!playing || activeControlsRef.current.has(gameControl)) return;

        activeControlsRef.current.add(gameControl);
        pressKey(gameControl);
        if (event.pointerType !== "mouse" && "vibrate" in navigator) {
            navigator.vibrate(10);
        }
    };

    const handlePointerEnd = (
        gameControl: string,
        event: ReactPointerEvent<HTMLDivElement>,
    ): void => {
        event.preventDefault();
        if (!activeControlsRef.current.delete(gameControl)) return;

        releaseKey(gameControl);
    };

    useEffect(() => {
        if (!playing) releaseActiveControls();
    }, [playing]);

    useEffect(() => releaseActiveControls, []);

    return (
        <GamePadWrapperGrid
            orientation={Orientation.vertical}
            gap={GapSize.medium}
        >
            <CssGrid
                orientation={Orientation.horizontal}
                gap={GapSize.medium}
                template="auto 1fr 1fr auto auto"
            >
                <DirectionalPad
                    onPointerStart={handlePointerStart}
                    onPointerEnd={handlePointerEnd}
                />
                <div />
                <div />
                <BGrid
                    alignItems={Position.center}
                    justifyContent={Position.center}
                    aria-label={gameControls.b}
                    onPointerDown={event =>
                        handlePointerStart(gameControls.b, event)
                    }
                    onPointerUp={event =>
                        handlePointerEnd(gameControls.b, event)
                    }
                    onPointerCancel={event =>
                        handlePointerEnd(gameControls.b, event)
                    }
                    className={gameControlClass}
                >
                    B
                </BGrid>
                <AGrid
                    alignItems={Position.center}
                    justifyContent={Position.center}
                    aria-label={gameControls.a}
                    onPointerDown={event =>
                        handlePointerStart(gameControls.a, event)
                    }
                    onPointerUp={event =>
                        handlePointerEnd(gameControls.a, event)
                    }
                    onPointerCancel={event =>
                        handlePointerEnd(gameControls.a, event)
                    }
                    className={gameControlClass}
                >
                    A
                </AGrid>
            </CssGrid>
            <CssGrid
                orientation={Orientation.horizontal}
                gap={GapSize.medium}
                template={isTablet ? "1fr auto 4fr auto 1fr" : undefined}
                justifyContent={!isTablet ? Position.center : undefined}
            >
                <div />
                <CylindricalButtonGrid
                    orientation={Orientation.vertical}
                    alignItems={Position.center}
                    justifyItems={Position.center}
                >
                    <CylindricalButtonGridArea
                        aria-label={gameControls.select}
                        onPointerDown={event =>
                            handlePointerStart(gameControls.select, event)
                        }
                        onPointerUp={event =>
                            handlePointerEnd(gameControls.select, event)
                        }
                        onPointerCancel={event =>
                            handlePointerEnd(gameControls.select, event)
                        }
                        className={gameControlClass}
                    />
                    <div>SELECT</div>
                </CylindricalButtonGrid>
                <div />
                <CylindricalButtonGrid
                    orientation={Orientation.vertical}
                    alignItems={Position.center}
                    justifyItems={Position.center}
                >
                    <CylindricalButtonGridArea
                        aria-label={gameControls.start}
                        onPointerDown={event =>
                            handlePointerStart(gameControls.start, event)
                        }
                        onPointerUp={event =>
                            handlePointerEnd(gameControls.start, event)
                        }
                        onPointerCancel={event =>
                            handlePointerEnd(gameControls.start, event)
                        }
                        className={gameControlClass}
                    />
                    <div>START</div>
                </CylindricalButtonGrid>
                <div />
            </CssGrid>
        </GamePadWrapperGrid>
    );
};

interface DirectionalPadProps {
    readonly onPointerStart: (
        gameControl: string,
        event: ReactPointerEvent<HTMLDivElement>,
    ) => void;
    readonly onPointerEnd: (
        gameControl: string,
        event: ReactPointerEvent<HTMLDivElement>,
    ) => void;
}

interface GamePadProps {
    readonly playing: boolean;
}

export default GamePad;
